import {createContext, useContext, Dispatch, SetStateAction} from 'react';
import {PreReviewForm} from '@/components/pages/tasks/task_edit/progress/pre_review/preReviewHelpers';

/** Ordered sub-steps of the Care Journey pre-review wizard (CARECO-5758). */
export const PRE_REVIEW_STEP_LABELS = [
  'Review Client Profile',
  'Review Referral',
  'Create Care Journey',
] as const;

export const PRE_REVIEW_STEP_COUNT = PRE_REVIEW_STEP_LABELS.length;

/**
 * Shared state for the pre-review wizard. The host progress rail (TaskEditProgress) owns the
 * `stepIndex` so the rail and the step content (CareJourneyPreReview) stay in sync — the component
 * no longer renders its own stepper (CARECO-6078).
 *
 * The content component registers a `stepGuard` via `setStepGuard`; the rail runs it before moving
 * forward so progress-rail clicks enforce the same per-step validation as the Next button and can't
 * mark an invalid step Completed (CARECO-6080).
 *
 * `dismissedBanners`/`dismissBanner` track dismissed instruction banners by stable id. They live on
 * the host (which outlives per-step unmounts) so a banner dismissed on one step stays dismissed
 * when navigating away and back; it resets when the task is re-opened (CARECO-6086).
 *
 * `form`/`setForm` are the reviewer's working set, also hosted so unsaved field edits and picker
 * selections survive the content unmount when the reviewer opens the Details view and returns
 * (CARECO-6283/4583). The host seeds it from the step prefill and resets it when the task changes.
 */
export interface PreReviewWizardValue {
  stepIndex: number;
  setStepIndex: (index: number) => void;
  setStepGuard: (guard: ((target: number) => boolean) | null) => void;
  dismissedBanners: Set<string>;
  dismissBanner: (id: string) => void;
  form: PreReviewForm;
  setForm: Dispatch<SetStateAction<PreReviewForm>>;
}

export const PreReviewWizardContext = createContext<PreReviewWizardValue | undefined>(undefined);

export function usePreReviewWizard(): PreReviewWizardValue {
  const context = useContext(PreReviewWizardContext);
  if (!context) {
    throw new Error('usePreReviewWizard must be used within a PreReviewWizardContext.Provider');
  }
  return context;
}
